import type { ReactNode, RefObject } from "react";
import { Children, createRef } from "react";

import clsx from "clsx";

import ChevronLeft from "@/design-system/icons/chevron-left";
import ChevronRight from "@/design-system/icons/chevron-right";

import type { IComponent } from "../Component";
import { Component } from "../Component";
import { isNumber, requestTrack, urlFormat } from "../Modules/Helpers";
import type { ISlidePosition } from "./CarouselHelper";
import {
    CarouselAnimationAppearance,
    addClassListArray,
    applyTransform,
    isAllVisible,
    lazyLoadingThresholdMax,
    lazyLoadingThresholdMin,
    positionX,
    removeClassListArray,
    scrollerBoundX,
    toggleAnimation,
    visibleIndexes,
} from "./CarouselHelper";
import Impetus from "./Impetus";

import * as css from "./Carousel.module.css";

export interface ICarousel extends IComponent {
    children: ReactNode[];
    slidesToScroll?: number;
    selectedIndex?: number;
    slideIndex?: boolean;
    animateOnInitialSelect?: boolean;
    freeScroll?: boolean;
    disableScroll?: boolean;
    mouseScroll?: boolean;
    edgeBounce?: boolean;
    friction?: number;
    autoplay?: boolean;
    autoplayInterval?: number;
    autoplayPauseHover?: boolean;
    resetAutoplay?: boolean;
    infinite?: boolean;
    centerMode?: boolean;
    showArrows?: boolean;
    showDisableArrows?: boolean;
    positionX?: number;
    positionXAnimate?: boolean;
    gestureSlide?: boolean;
    gestureSlideMin?: number;
    gestureSlideMax?: number;
    onSlide?: (activeIndex: number | null, visibleIndexes: number[], positionX: number) => void;
    lazyLoading?: boolean;
    lazyLoadingThreshold?: number;
    bindKeys?: boolean;
    numberElements?: number;
    carouselAnimationAppearance?: CarouselAnimationAppearance;
    arrowTrackAction?: string;
    carouselRef?: RefObject<HTMLDivElement>;
    classNameContainer?: string;
    classNameSlide?: string;
}

export class Carousel extends Component<ICarousel> {
    private mounted = false;

    private clientWidth = 0;

    private container = createRef<HTMLUListElement>();

    private prevArrowRef = createRef<HTMLButtonElement>();

    private nextArrowRef = createRef<HTMLButtonElement>();

    private containerTranslate = 0;

    private containerWidth = 0;

    private accumulatedWidth = 0;

    private slidesElement: HTMLElement[] = [];

    private positions: ISlidePosition[] = [];

    private lastPosition = 0;

    private active: ISlidePosition[] = [];

    private lastActive = 0;

    private impetus: any = null;

    private isScrolling = false;

    private isManualScroll = false;

    private activeIndex: number;

    private autoplay: number;

    private forceScroll = false;

    private resizeTimeout: number;

    private preventOnClick = false;

    private initialClientX = 0;

    private initialClientY = 0;

    private defaultScroll = false;

    private touchEventId: number | null = null;

    private multiTouch = false;

    private activeIndexFade: number;

    private autoPlayFadeTimeout: any;

    private isTouchScreenMode =
        typeof window !== "undefined" ? "ontouchstart" in window || navigator.maxTouchPoints > 0 : false;

    public static defaultProps: ICarousel = {
        children: [],
        slidesToScroll: 1,
        selectedIndex: -1,
        slideIndex: false,
        animateOnInitialSelect: false,
        freeScroll: false,
        disableScroll: false,
        mouseScroll: false,
        edgeBounce: true,
        friction: 0.92,
        autoplay: false,
        autoplayInterval: 2000,
        autoplayPauseHover: false,
        infinite: false,
        centerMode: false,
        showArrows: true,
        showDisableArrows: false,
        positionX: 0,
        positionXAnimate: true,
        gestureSlide: true,
        gestureSlideMin: 30,
        gestureSlideMax: 100,
        lazyLoading: false,
        lazyLoadingThreshold: 5,
        carouselAnimationAppearance: CarouselAnimationAppearance.Standard,
    };

    constructor(props: ICarousel) {
        super(props);

        this.activeIndex =
            props.selectedIndex &&
            props.selectedIndex >= 0 &&
            props.selectedIndex < this.props.children.length
                ? props.selectedIndex
                : 0;

        this.activeIndexFade =
            props.carouselAnimationAppearance === CarouselAnimationAppearance.Fade ? this.activeIndex : -1;
    }

    public componentDidMount() {
        if (!this.props.children) {
            return;
        }

        if (
            this.props.carouselAnimationAppearance === CarouselAnimationAppearance.Fade &&
            this.props.autoplay === true
        ) {
            this.autoPlayFade();
        }

        this.clientWidth = window.innerWidth;

        this.calculatePositions();

        if (this.props.selectedIndex) {
            if (this.props.animateOnInitialSelect === false) {
                toggleAnimation(this.container.current, false);
            }

            const selected = this.positions[this.props.selectedIndex];
            const skipTranslate =
                this.props.centerMode === false &&
                this.isScrolling === false &&
                isAllVisible(selected, this.containerWidth);
            this.goto(selected, skipTranslate, this.props.animateOnInitialSelect);
        } else if (this.props.positionX && this.props.positionX !== this.containerTranslate) {
            if (this.props.positionXAnimate === false) {
                toggleAnimation(this.container.current, false);
            }

            this.translateContainer(positionX(this.props.positionX));
            this.calculatePositions();
            this.activeIndex = this.firstActive ? this.firstActive.index : 0;
        }

        if (this.props.bindKeys) {
            window.addEventListener("keyup", this.onKeyPress);
        }

        if (this.container.current) {
            this.container.current.addEventListener("touchstart", this.onTouchStart);
            this.container.current.addEventListener("mousedown", this.onMouseDown);
        }

        this.scrollerInitialize();

        this.setAutoplay();

        this.ensureArrowsVisibility();

        this.mounted = true;

        window.addEventListener("resize", this.handleResize);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: ICarousel) {
        if (!this.impetus) {
            this.scrollerInitialize();
        }

        if (this.props.autoplay === true && nextProps.autoplay === false && this.autoplay) {
            clearInterval(this.autoplay);
        }

        if (this.props.autoplay === false && nextProps.autoplay === true) {
            if (this.props.carouselAnimationAppearance === CarouselAnimationAppearance.Fade) {
                this.autoPlayFade();
            } else {
                this.setAutoplay(nextProps);
            }
        }

        if (nextProps.autoplay === true && this.props.autoplayInterval !== nextProps.autoplayInterval) {
            this.setAutoplay(nextProps);
        }

        if (nextProps.resetAutoplay === true) {
            this.setAutoplay(nextProps);
        }

        if (this.props.bindKeys) {
            window.addEventListener("keyup", this.onKeyPress);
        } else {
            window.removeEventListener("keyup", this.onKeyPress);
        }
    }

    public componentDidUpdate(prevProps: ICarousel) {
        if (!prevProps.children && !this.props.children) {
            return;
        }

        if (this.props.centerMode !== prevProps.centerMode) {
            this.calculatePositions();
            this.goto(this.activeIndex);

            if (this.impetus) {
                this.impetus.setBoundX(scrollerBoundX(this.accumulatedWidth, this.containerWidth));
            }
        }

        const posX = positionX(this.props.positionX);

        if (
            isNumber(prevProps.selectedIndex) &&
            isNumber(this.props.selectedIndex) &&
            prevProps.selectedIndex !== this.props.selectedIndex &&
            this.props.selectedIndex > -1
        ) {
            this.calculatePositions();
            const selected = this.positions[this.props.selectedIndex];
            const skipTranslate =
                this.props.centerMode === false &&
                this.isScrolling === false &&
                isAllVisible(selected, this.containerWidth);
            this.goto(selected, skipTranslate);
        } else if (posX !== 0 && posX !== this.containerTranslate) {
            if (this.props.positionXAnimate === false) {
                toggleAnimation(this.container.current, false);
            }

            this.translateContainer(posX);

            this.calculatePositions();
            this.activeIndex = (this.firstActive && this.firstActive.index) || 0;
        }

        if (!prevProps.children && this.props.children) {
            this.calculatePositions();
            return;
        }

        if (prevProps.children.length !== this.props.children.length) {
            this.calculatePositions();
            this.ensureArrowsVisibility();

            if ([...this.active].pop() === [...this.positions].pop()) {
                this.goto(this.activeIndex - 1);
            }

            if (this.impetus) {
                this.impetus.setBoundX(scrollerBoundX(this.accumulatedWidth, this.containerWidth));
            }
        }
    }

    public componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);

        this.mounted = false;

        if (this.container.current) {
            this.container.current.removeEventListener("touchstart", this.onTouchStart);
            this.container.current.removeEventListener("mousedown", this.onMouseDown);
        }

        if (this.impetus) {
            this.impetus.destroy();
        }

        if (this.autoplay) {
            clearInterval(this.autoplay);
        }

        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        if (this.autoPlayFadeTimeout) {
            clearTimeout(this.autoPlayFadeTimeout);
        }

        if (this.props.bindKeys) {
            window.removeEventListener("keyup", this.onKeyPress);
        }
    }

    private onClickArrow = (isNext: boolean) => {
        if (isNext) {
            this.slideNext();

            if (this.showNextArrow === true) {
                this.callArrowTelemetry("4");
            }
        } else {
            this.slidePrevious();

            if (this.showPreviousArrow === true) {
                this.callArrowTelemetry("3");
            }
        }
    };

    private get centerActive(): ISlidePosition {
        const middle = this.containerWidth / 2;
        let closestMiddle = null;

        const firstActive = this.active[0];

        if (firstActive === this.positions[0] && firstActive && firstActive.left >= 0) {
            return firstActive;
        }

        const lastActive = [...this.active].pop();

        if (
            lastActive === [...this.positions].pop() &&
            lastActive &&
            lastActive.right <= this.containerWidth
        ) {
            return lastActive;
        }

        let closestItem = undefined as unknown as ISlidePosition;

        for (let i = 0; i < this.active.length; i += 1) {
            const item = this.active[i];
            const itemMiddle = (item.right + item.left) / 2;
            const diff = Math.abs(middle - itemMiddle);

            if (closestMiddle === null) {
                closestMiddle = diff;
            }

            if (diff <= closestMiddle) {
                closestMiddle = diff;
                closestItem = item;
            }
        }

        return closestItem;
    }

    private get firstActive(): ISlidePosition | null {
        if (!this.active) {
            return null;
        }

        const firstVisible = this.active[0];
        const secondVisible = this.active[1];

        if (!firstVisible) {
            return null;
        }

        if (!secondVisible) {
            return firstVisible;
        }

        const firstMiddle = (firstVisible.right + firstVisible.left) / 2;

        if (firstMiddle < 0) {
            return secondVisible;
        }

        return firstVisible;
    }

    private get showNextArrow() {
        if (
            this.active &&
            this.active.length > 0 &&
            this.props.showArrows === true &&
            this.props.infinite === false
        ) {
            const last = [...this.positions].pop();
            const lastActive = [...this.active].pop();

            if (last === lastActive && lastActive && lastActive.right <= this.containerWidth) {
                return false;
            }
        } else if (!this.active || this.active.length === 0) {
            return false;
        }

        return true;
    }

    private get showPreviousArrow() {
        if (
            this.active &&
            this.active.length > 0 &&
            this.props.showArrows === true &&
            this.props.infinite === false
        ) {
            const first = this.positions[0];
            const firstActive = this.active[0];

            if (first === firstActive && firstActive && firstActive.left >= 0) {
                return false;
            }
        } else if (!this.active || this.active.length === 0) {
            return false;
        }

        return true;
    }

    private get previousArrow() {
        if (this.props.showArrows === false) {
            return null;
        }

        const prevArrowClass = clsx(
            css.arrow,
            css.previousArrow,
            this.isTouchScreenMode && css.isTouchMode,
            this.mounted === false || this.showPreviousArrow === false
                ? this.props.showDisableArrows === true
                    ? css.disable
                    : css.hide
                : null,
        );

        return (
            <button
                ref={this.prevArrowRef}
                className={prevArrowClass}
                onClick={() => this.onClickArrow(false)}
                type="button"
            >
                <ChevronLeft />
            </button>
        );
    }

    private get nextArrow() {
        if (this.props.showArrows === false) {
            return null;
        }

        const nextArrowClass = clsx(
            css.arrow,
            css.nextArrow,
            this.isTouchScreenMode && css.isTouchMode,
            this.mounted === false || this.showNextArrow === false
                ? this.props.showDisableArrows === true
                    ? css.disable
                    : css.hide
                : null,
        );

        return (
            <button
                ref={this.nextArrowRef}
                className={nextArrowClass}
                onClick={() => this.onClickArrow(true)}
                type="button"
            >
                <ChevronRight />
            </button>
        );
    }

    private callArrowTelemetry = (param: string) => {
        if (this.props.arrowTrackAction) {
            requestTrack(urlFormat(this.props.arrowTrackAction, { id: param }, true));
        }
    };

    private handleResize = () => {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        const { innerWidth } = window;

        if (innerWidth === this.clientWidth) {
            return;
        }

        this.clientWidth = innerWidth;

        this.resizeTimeout = window.setTimeout(() => {
            const prevContainerWidth = this.containerWidth;

            this.calculatePositions();

            this.ensureArrowsVisibility();

            if (prevContainerWidth === this.containerWidth) {
                return;
            }

            if (this.impetus) {
                this.impetus.destroy();
            }

            const active = this.positions[this.activeIndex];
            if (active) {
                this.goto(active);
            } else {
                this.goto(this.positions[0]);
            }

            this.scrollerInitialize();
        }, 200);
    };

    private autoPlayFade = () => {
        if (this.activeIndexFade != null) {
            this.autoPlayFadeTimeout = window.setTimeout(() => {
                this.activeIndexFade += 1;

                if (this.activeIndexFade > this.props.children.length - 1) {
                    this.activeIndexFade = 0;
                }

                if (this.props.onSlide) {
                    this.props.onSlide(this.activeIndexFade, [], 0);
                }
                this.autoPlayFade();
            }, this.props.autoplayInterval || 2000);
        }
    };

    private onKeyPress = (e: KeyboardEvent) => {
        if (e.keyCode === 39) {
            this.slideNext();
        } else if (e.keyCode === 37) {
            this.slidePrevious();
        }
    };

    private setAutoplay = (props = this.props) => {
        if (props.autoplay === false) {
            return;
        }

        if (props.carouselAnimationAppearance === CarouselAnimationAppearance.Fade) {
            return;
        }

        const interval = props.autoplayInterval
            ? props.autoplayInterval
            : Carousel.defaultProps.autoplayInterval;

        if (this.autoplay) {
            clearInterval(this.autoplay);
        }

        this.autoplay = window.setInterval(() => {
            if (this.isManualScroll === true) {
                return;
            }

            this.slideNext();
        }, interval);
    };

    private scrollerInitialize = () => {
        if (!this.container.current) {
            return;
        }

        let isScrollFinishing = false;
        let finishTimeout: any = null;

        const setFinish = () => {
            this.isScrolling = false;
            isScrollFinishing = true;
            this.finishScroll();
        };

        const finalizeScrolling = (x: number) => {
            const variation = Math.abs(Math.abs(x) - Math.abs(this.containerTranslate));
            const variationMax = this.props.freeScroll === true ? 0.5 : 5;

            const shouldFinish = variation >= 0 && variation < variationMax;

            isScrollFinishing = this.isManualScroll ? false : isScrollFinishing;

            if (finishTimeout) {
                clearTimeout(finishTimeout);
            }

            if (this.isManualScroll) {
                this.isScrolling = false;
                return false;
            }

            if (isScrollFinishing === true) {
                return true;
            }

            if (x > 0) {
                if (x < 1 && isScrollFinishing === false) {
                    setFinish();
                    return true;
                }

                return false;
            }

            const xAbs = Math.abs(x);
            const rightEdge = this.accumulatedWidth - this.containerWidth;

            if (xAbs > rightEdge) {
                if (isScrollFinishing === false && xAbs > rightEdge && xAbs < rightEdge + 1) {
                    setFinish();
                    return true;
                }

                return false;
            }

            if (isScrollFinishing === false && shouldFinish) {
                setFinish();
                return true;
            }

            // just to ensure that we finish the scroll in case any of the previous fails.
            finishTimeout = window.setTimeout(() => {
                setFinish();
            }, 100);

            return false;
        };

        this.impetus = new Impetus({
            source: this.container.current,
            initialValues: [this.containerTranslate, 0],
            boundX: scrollerBoundX(this.accumulatedWidth, this.containerWidth),
            bounce: this.props.friction === 0 ? false : this.props.edgeBounce,
            friction: this.props.friction,
            update: (x: number) => {
                if (this.mounted === false) {
                    return;
                }

                if (this.forceScroll === true) {
                    return;
                }

                if (x === this.containerTranslate) {
                    return;
                }

                this.isScrolling = true;

                const finalize = finalizeScrolling(x);

                if (finalize === true) {
                    return;
                }

                toggleAnimation(this.container.current, false);
                this.containerTranslate = x;

                if (this.mounted === true && this.container.current) {
                    applyTransform(x, this.container.current);
                }
            },
        });
    };

    private finishScroll = () => {
        if (this.mounted === false) {
            return;
        }

        const previousActive = this.active.map((a) => a.index);

        this.calculatePositions();

        let activeSlide: ISlidePosition | null;

        if (this.props.slideIndex === true) {
            activeSlide = this.positions[this.activeIndex];
        } else if (this.props.centerMode === true) {
            activeSlide = this.centerActive;
        } else {
            activeSlide = this.firstActive;
        }

        if (!activeSlide) {
            return;
        }

        const hasChanged = previousActive.join(",") !== this.active.map((p) => p.index).join(",");

        const previousIndex = this.activeIndex;

        const slide = () => {
            if (!activeSlide) {
                return;
            }

            if (previousIndex !== this.activeIndex) {
                this.onSlide(activeSlide);
                return;
            }

            this.goto(activeSlide);

            if (this.activeIndex !== previousIndex) {
                this.onSlideCallback(activeSlide);
            }
        };

        if (hasChanged === true) {
            this.activeIndex = activeSlide.index;

            if (this.props.freeScroll === true || this.props.slideIndex === true) {
                this.ensureArrowsVisibility();
                this.onSlideCallback(activeSlide);

                if (this.props.freeScroll === false) {
                    this.adjustToIndex(this.active[0].index);
                }

                this.ensureLazyLoading();

                return;
            }

            slide();
        } else if (activeSlide && this.props.freeScroll === false) {
            slide();
        } else {
            this.ensureArrowsVisibility();
        }
    };

    private ensureLazyLoading = () => {
        if (!this.props.lazyLoading) {
            return;
        }

        const thresholdMin = lazyLoadingThresholdMin(this.activeIndex, this.props.lazyLoadingThreshold);

        const thresholdMax = lazyLoadingThresholdMax(this.activeIndex, this.props.lazyLoadingThreshold);

        const min = thresholdMin < 0 ? 0 : thresholdMin;
        const max = thresholdMax > this.positions.length ? this.lastPosition : thresholdMax;

        // add active item
        const nrRendered = max - min + 1;

        if (this.positions.length <= nrRendered) {
            return;
        }

        this.forceUpdate();
    };

    private calculatePositions = () => {
        if (this.props.carouselAnimationAppearance === CarouselAnimationAppearance.Fade) {
            return;
        }

        this.positions = [];
        this.active = [];
        this.accumulatedWidth = 0;

        if (this.container?.current?.parentElement) {
            this.slidesElement.forEach((x) => x.removeAttribute("style"));
        }
        const elemWidth =
            this.slidesElement?.length > 0
                ? this.slidesElement[this.slidesElement.length - 1].offsetWidth
                : 0;

        let accOffset = this.containerTranslate;
        let count = 0;
        let containerWidth = 0;

        for (let i = 0; i < this.slidesElement.length; i += 1) {
            const slide = this.slidesElement[i];
            slide.removeAttribute("style");
            const left = slide.offsetLeft + this.containerTranslate;
            const right = accOffset + slide.offsetWidth;

            containerWidth = (slide.parentElement && slide.parentElement.offsetWidth) || 0;

            accOffset += slide.offsetWidth;
            this.accumulatedWidth += slide.offsetWidth;

            const obj = {
                left,
                right,
                index: count,
                width: slide.clientWidth,
            };

            if (this.props.numberElements) {
                slide.style.width = `${elemWidth}px`;
                if (left >= 0 && left < containerWidth + 5) {
                    this.active.push(obj);
                }
            }

            if (left >= 0 && left < containerWidth && !this.props.numberElements) {
                this.active.push(obj);
            }

            if (left < 0 && right > 0) {
                this.active.push(obj);
            }

            this.positions.push(obj);

            count += 1;
        }

        this.lastActive = this.active.length - 1;
        this.lastPosition = this.positions.length - 1;
        this.containerWidth = containerWidth;
    };

    private addSlide = (el: HTMLElement | null) => el && this.slidesElement.push(el);

    private goto = (slide: any, skipTranslate = false, animate = true) => {
        if (slide === null || slide === undefined) {
            return false;
        }

        let index: number = this.activeIndex;

        if (typeof slide === "number") {
            index = slide;
        }

        if (typeof slide === "object") {
            index = (slide as ISlidePosition).index;
        }

        this.activeIndex = index;

        return this.adjustToIndex(this.activeIndex, skipTranslate, animate);
    };

    private adjustToIndex = (index: number, skipTranslate = false, animate = true) => {
        const translate = this.getTranslate(index, skipTranslate);

        if (!translate) {
            return false;
        }

        const nextTranslate = this.containerTranslate + translate;

        if (nextTranslate === this.containerTranslate) {
            this.calculatePositions();
            return false;
        }

        toggleAnimation(this.container.current, animate);

        this.translateContainer(nextTranslate);

        this.calculatePositions();

        this.ensureArrowsVisibility();

        return true;
    };

    private ensureArrowsVisibility = () => {
        if (this.props.showArrows === true) {
            if (this.showNextArrow === false) {
                addClassListArray(
                    this.nextArrowRef.current,
                    this.props.showDisableArrows ? css.disable : css.hide,
                );
            } else {
                removeClassListArray(
                    this.nextArrowRef.current,
                    this.props.showDisableArrows ? css.disable : css.hide,
                );
            }

            if (this.showPreviousArrow === false) {
                addClassListArray(
                    this.prevArrowRef.current,
                    this.props.showDisableArrows ? css.disable : css.hide,
                );
            } else {
                removeClassListArray(
                    this.prevArrowRef.current,
                    this.props.showDisableArrows ? css.disable : css.hide,
                );
            }
        }
    };

    private translateContainer = (value: number) => {
        if (value > 0) {
            this.containerTranslate = 0;
        } else if (Math.abs(value - this.containerWidth) > this.accumulatedWidth) {
            this.containerTranslate = (this.accumulatedWidth - this.containerWidth) * -1;
        } else {
            this.containerTranslate = value;
        }

        if (this.impetus) {
            this.impetus.setValues(this.containerTranslate);
        }

        if (this.container?.current?.parentElement && this.props.numberElements) {
            if (this.props.slidesToScroll === this.props.numberElements) {
                const elementWidth = this.container.current.parentElement.offsetWidth || 0;
                const roundWidthValue = Math.round(this.containerTranslate / elementWidth);
                this.containerTranslate = elementWidth * roundWidthValue;
            }
        }

        applyTransform(this.containerTranslate, this.container.current);
    };

    private onSlide = (slide: ISlidePosition, skipTranslate = false) => {
        this.goto(slide, skipTranslate);

        this.onSlideCallback(slide);

        this.ensureLazyLoading();
    };

    private onSlideCallback = (slide: ISlidePosition) => {
        if (this.props.onSlide) {
            this.props.onSlide(
                slide ? slide.index : null,
                visibleIndexes(this.active),
                Math.abs(this.containerTranslate),
            );
        }
    };

    private getTranslate = (index: number, skipTranslate = false): number | null => {
        if (index === null) {
            return null;
        }

        const last = this.positions[this.lastPosition];
        const pos = this.positions[index];

        if (pos === null || pos === undefined) {
            return 0;
        }

        if (skipTranslate === true && pos.left >= 0 && pos.right <= this.containerWidth) {
            return 0;
        }

        let rightDiff = Math.abs(this.containerWidth - pos.right);
        let translate = pos.left * -1;
        let isLeft = true;

        if (rightDiff < Math.abs(pos.left)) {
            if (pos.right > this.containerWidth) {
                rightDiff *= -1;
            }

            translate = rightDiff;
            isLeft = false;
        }

        let centerPadding = 0;

        if (this.props.centerMode === true && index !== this.positions[this.lastPosition].index) {
            centerPadding = (this.containerWidth - this.positions[index].width) / 2;

            if (isLeft) {
                translate += centerPadding;
            } else {
                translate -= centerPadding;
            }
        }

        const leftPos = pos.left - centerPadding;

        if (isLeft && last.right - leftPos < this.containerWidth) {
            translate = (last.right - this.containerWidth) * -1;
        }

        return translate;
    };

    public slideNext = () => {
        if (!this.container || this.lastActive + 1 === 0) {
            return;
        }

        if (this.props.autoplay) {
            this.setAutoplay();
        }

        if (this.impetus && this.isScrolling === true) {
            this.isScrolling = false;
            this.finishScroll();
        }

        const a = this.active.filter((i) => i.index === this.activeIndex);
        let active = null;

        if (a) {
            active = a[a.length - 1];
        }

        if (!active || this.props.slideIndex === false) {
            active = this.active[this.lastActive];
        }

        let { index } = active;

        if (active.right > this.containerWidth) {
            index -= 1;
        }

        index += this.props.slidesToScroll || 0;

        let next = this.positions[index];
        if (!next) {
            const last = this.positions[this.lastPosition];
            const isLast = this.activeIndex === last.index || last.right <= this.containerWidth;

            next =
                this.props.infinite === false
                    ? this.positions[this.lastPosition]
                    : isLast
                      ? this.positions[0]
                      : last;
        }

        if (this.activeIndex !== this.positions[this.lastPosition].index || next.index !== this.activeIndex) {
            this.onSlide(next, this.props.slideIndex === true);
        }
    };

    public slidePrevious = () => {
        if (!this.container || this.active.length === 0) {
            return;
        }

        if (this.props.autoplay) {
            this.setAutoplay();
        }

        if (this.impetus && this.isScrolling === true) {
            this.isScrolling = false;
            this.finishScroll();
        }

        let active = this.active.filter((a) => a.index === this.activeIndex)?.pop();

        if (!active || this.props.slideIndex === false) {
            [active] = this.active;
        }

        let { index } = active;

        if (active.left < 0) {
            index += 1;
        }

        index -= this.props.slidesToScroll || 0;

        let previous = this.positions[index];

        if (!previous) {
            const first = this.positions[0];
            const isFirst = this.activeIndex === first.index || first.left >= 0;

            previous =
                this.props.infinite === false
                    ? this.positions[0]
                    : isFirst
                      ? this.positions[this.lastPosition]
                      : first;
        }

        if (this.activeIndex !== 0 || previous.index !== this.activeIndex) {
            this.onSlide(previous, this.props.slideIndex === true);
        }
    };

    private onMouseDown = (event: MouseEvent) => {
        if (this.props.disableScroll === true) {
            event.stopImmediatePropagation();
            return;
        }

        window.addEventListener("mouseup", this.onMouseUp);

        if (this.props.mouseScroll === false) {
            this.impetus.pause();
            return;
        }

        if (this.container.current) {
            this.container.current.addEventListener("mousemove", this.onMove);
        }

        this.initialClientX = event.clientX;
        this.initialClientY = event.clientY;

        this.isManualScroll = false;
        this.isScrolling = false;
        this.forceScroll = false;
        this.touchEventId = null;
        this.multiTouch = false;
        this.defaultScroll = false;

        if (this.impetus) {
            this.impetus.setValues(this.containerTranslate);
        }
    };

    private onTouchStart = (event: TouchEvent) => {
        if (this.props.disableScroll === true) {
            event.stopImmediatePropagation();
            return;
        }

        const touch = event.changedTouches[0] || event.targetTouches[0];

        this.multiTouch = this.touchEventId !== null;

        this.touchEventId = touch.identifier;

        document.addEventListener("touchmove", this.onMove);
        document.addEventListener("touchend", this.onTouchEnd);
        document.addEventListener("touchcancel", this.onTouchEnd);

        this.initialClientX = touch.clientX;
        this.initialClientY = touch.clientY;

        this.isManualScroll = false;
        this.isScrolling = false;
        this.forceScroll = false;
        this.defaultScroll = false;

        if (this.impetus) {
            this.impetus.setValues(this.containerTranslate);
        }
    };

    private onMove = (event: TouchEvent | MouseEvent) => {
        this.preventOnClick = true;

        if (this.multiTouch === true) {
            // allow zoom in/out
            event.stopImmediatePropagation();
            return;
        }

        if (this.isManualScroll === true) {
            return;
        }

        if (this.defaultScroll === true) {
            event.stopImmediatePropagation();
            return;
        }

        let diffX = 0;
        let diffY = 0;

        const touchEvent = event as TouchEvent;
        const mouseEvent = event as MouseEvent;

        if (touchEvent.changedTouches || touchEvent.targetTouches) {
            const touch = touchEvent.changedTouches[0] || touchEvent.targetTouches[0];

            diffX = Math.abs(touch.clientX - this.initialClientX);
            diffY = Math.abs(touch.clientY - this.initialClientY);
        } else if (mouseEvent.clientX !== undefined && mouseEvent.clientY !== undefined) {
            if (this.props.mouseScroll === false) {
                event.stopImmediatePropagation();
                return;
            }

            diffX = Math.abs(mouseEvent.clientX - this.initialClientX);
            diffY = Math.abs(mouseEvent.clientY - this.initialClientY);
        }

        if (diffX > diffY) {
            this.isManualScroll = true;
        } else {
            this.defaultScroll = true;
            event.stopImmediatePropagation();
        }
    };

    private onMouseUp = () => {
        if (this.props.mouseScroll === false) {
            this.impetus.resume();
            return;
        }

        this.isManualScroll = false;
        this.defaultScroll = false;

        window.setTimeout(() => {
            this.preventOnClick = false;
            if (this.isScrolling === false && this.props.mouseScroll === true) {
                this.finishScroll();
            }
        }, 100);

        if (this.container.current) {
            this.container.current.removeEventListener("mousemove", this.onMove);
        }

        window.removeEventListener("mouseup", this.onMouseUp);
    };

    private onTouchEnd = (event: TouchEvent) => {
        const touch = event.changedTouches[0] || event.targetTouches[0];

        this.finalizeTouchEvent();

        if (this.defaultScroll === false && this.multiTouch === false) {
            this.checkGestureSlide(touch.clientX);
        }
    };

    private onTouchCancel = () => {
        this.finalizeTouchEvent();
    };

    private finalizeTouchEvent = () => {
        this.isManualScroll = false;
        this.defaultScroll = false;
        this.touchEventId = null;

        window.setTimeout(() => {
            this.preventOnClick = false;

            if (this.isScrolling === false) {
                this.finishScroll();
            }
        }, 100);

        document.removeEventListener("touchmove", this.onMove);
        document.removeEventListener("touchend", this.onTouchEnd);
        document.removeEventListener("touchcancel", this.onTouchCancel);
    };

    private onClickPrevent = (event: React.MouseEvent<HTMLLIElement>) => {
        if (this.preventOnClick === true) {
            event.stopPropagation();
            event.preventDefault();
        }

        this.preventOnClick = false;
    };

    private checkGestureSlide = (clientX: number) => {
        if (this.props.freeScroll === true || this.props.gestureSlide === false) {
            return;
        }

        const diffX = clientX - this.initialClientX;

        const { gestureSlideMin = 30, gestureSlideMax = 100 } = this.props;

        if (
            diffX < gestureSlideMin * -1 &&
            diffX > gestureSlideMax * -1 &&
            this.activeIndex < this.positions[this.lastPosition].index
        ) {
            this.forceScroll = true;
            this.calculatePositions();
            this.slideNext();
        }

        if (diffX > gestureSlideMin && diffX < gestureSlideMax && this.activeIndex > 0) {
            this.forceScroll = true;
            this.calculatePositions();
            this.slidePrevious();
        }
    };

    private getSlides = () => {
        if (!this.props.children) {
            return null;
        }

        const bindHover = this.props.autoplay === true && this.props.autoplayPauseHover === true;

        const pauseAutoplay = () => {
            if (this.autoplay) {
                clearInterval(this.autoplay);
            }
        };

        const resumeAutoplay = () => {
            this.setAutoplay();
        };

        const renderChild = (child: React.ReactNode, index: number) => {
            if (!this.props.lazyLoading) {
                return child;
            }

            if (
                index >= lazyLoadingThresholdMin(this.activeIndex, this.props.lazyLoadingThreshold) &&
                index <= lazyLoadingThresholdMax(this.activeIndex, this.props.lazyLoadingThreshold)
            ) {
                return child;
            }

            return null;
        };

        const className = clsx(css.slide, this.props.classNameSlide);

        return Children.map(this.props.children, (c: React.ReactChild, index: number) => (
            <li
                key={index}
                style={
                    this.activeIndexFade >= 0
                        ? {
                              display:
                                  this.activeIndexFade < 0 || this.activeIndexFade === index
                                      ? "block"
                                      : "none",
                          }
                        : undefined
                }
                className={className}
                ref={(el) => this.addSlide(el)}
                onMouseOver={bindHover ? pauseAutoplay : undefined}
                onMouseOut={bindHover ? resumeAutoplay : undefined}
                onFocus={bindHover ? resumeAutoplay : undefined}
                onTouchStart={bindHover ? pauseAutoplay : undefined}
                onTouchEnd={bindHover ? resumeAutoplay : undefined}
                onBlur={bindHover ? resumeAutoplay : undefined}
                onClickCapture={this.onClickPrevent}
            >
                {renderChild(c, index)}
            </li>
        ));
    };

    public render(): JSX.Element | null {
        if (!this.props.children) {
            return null;
        }

        this.slidesElement = [];
        const className = [];

        if (this.props.centerMode === true && this.props.children.length > 1) {
            className.push(css.centerMode);
        }

        if (this.props.showArrows === true) {
            className.push(css.withArrows);
        }

        const sliderContainerClass = clsx(
            css.sliderContainer,
            this.props.classNameContainer,
            this.props.carouselAnimationAppearance === CarouselAnimationAppearance.Fade && css.fadeAppearance,
        );

        return (
            <div {...this.rootDefaultAttributes(css.carouselComponent, ...className)}>
                {this.previousArrow}
                <div className={css.carouselContainer} ref={this.props.carouselRef}>
                    <ul className={sliderContainerClass} ref={this.container}>
                        {this.getSlides()}
                    </ul>
                </div>
                {this.nextArrow}
            </div>
        );
    }
}
